import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ConfirmationResultModal, FormField, Select } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { GROUP_MEMBER_ROLES } from '@percent/admin-dashboard/api/actions/groups/groups.types'
import type { GroupMemberRole } from '@percent/admin-dashboard/api/actions/groups/groups.types'
import type { SelectOption } from '@percent/lemonade'
import type { ChangeRoleModalProps } from './ChangeRoleModal.types'

type ModalState = 'confirm' | 'success' | 'failure'

const getMemberDisplayName = (member: ChangeRoleModalProps['member']): string => {
  if (!member) return ''

  if (member.type === 'account') {
    return member.fullName || member.email
  }

  return member.name
}

export function ChangeRoleModal({ isOpen, onClose, groupId, member, onSuccess }: ChangeRoleModalProps) {
  const { t } = useTranslation()
  const { groupService } = useServices()
  const [modalState, setModalState] = useState<ModalState>('confirm')
  const [selectedRole, setSelectedRole] = useState<GroupMemberRole | null>(null)

  const [{ isPending }, { mutateAsync: changeRole }] = useMutation(groupService.changeGroupMemberRole)

  const roleOptions: SelectOption[] = useMemo(
    () =>
      GROUP_MEMBER_ROLES.map(role => ({
        value: role,
        label: t(`groupDetail.members.role.${role}`)
      })),
    [t]
  )

  const handleConfirm = async () => {
    if (!member || !selectedRole) return

    try {
      await changeRole({ groupId, memberId: member.id, role: selectedRole })
      setModalState('success')
    } catch {
      setModalState('failure')
    }
  }

  const handleClose = () => {
    if (!isPending) {
      setModalState('confirm')
      setSelectedRole(null)
      onClose()
    }
  }

  const handleSuccessClose = () => {
    setModalState('confirm')
    setSelectedRole(null)
    onSuccess?.()
    onClose()
  }

  const handleFailureClose = () => {
    setModalState('confirm')
    onClose()
  }

  if (!isOpen || !member) {
    return null
  }

  const hasRoleChanged = selectedRole && selectedRole !== member.role

  return (
    <ConfirmationResultModal
      isOpen={isOpen}
      state={modalState === 'confirm' ? undefined : modalState}
      title={t('modal.changeRole.title')}
      primaryButtonText={isPending ? t('modal.changeRole.button.saving') : t('button.saveChanges')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={handleConfirm}
      onClose={modalState === 'success' ? handleSuccessClose : handleClose}
      onFailureClose={handleFailureClose}
      loading={isPending}
      disabled={!hasRoleChanged}
      primaryBtnTestId="change-role-submit-button"
      secondaryBtnTestId="change-role-cancel-button"
      success={{
        title: t('modal.changeRole.success.title'),
        description: t('modal.changeRole.success.description', {
          name: getMemberDisplayName(member),
          role: selectedRole ? t(`groupDetail.members.role.${selectedRole}`) : ''
        }),
        button: t('button.close')
      }}
      failure={{
        title: t('modal.changeRole.error.title'),
        description: t('modal.changeRole.error.description', { name: getMemberDisplayName(member) }),
        button: t('button.close')
      }}
    >
      <FormField label={t('modal.changeRole.roleLabel')} necessity="required" data-testid="change-role-form-field">
        <Select
          name="role"
          placeholder={t('modal.changeRole.selectPlaceholder')}
          options={roleOptions}
          defaultValue={roleOptions.find(opt => opt.value === member.role)}
          onChange={(option: SelectOption) => {
            setSelectedRole(option.value as GroupMemberRole)
          }}
          data-testid="change-role-select"
        />
      </FormField>
    </ConfirmationResultModal>
  )
}
